import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useDynamicPWA = () => {
  
  const updateManifestLink = () => {
    // Remove existing manifest link
    const existingLink = document.querySelector('link[rel="manifest"]');
    if (existingLink) {
      document.head.removeChild(existingLink);
    }

    // Add new dynamic manifest link
    const manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    manifestLink.href = `https://ginefbvgxcobyuocxerb.supabase.co/functions/v1/dynamic-manifest?t=${Date.now()}`;
    document.head.appendChild(manifestLink);

    console.log('PWA: Updated manifest link');
  };

  const updateAppleTouchIcons = async () => {
    try {
      const { data: settings } = await supabase
        .from('admin_settings')
        .select('settings')
        .eq('id', 1)
        .single();

      if (settings?.settings?.pwa) {
        // Check both locations for custom icons
        const customIcons = {
          ...settings.settings.pwa.customIcons || {},
          ...settings.settings.pwa.manifest?.icons || {}
        };

        // Function to get icon URL with intelligent fallbacks optimized for Apple devices
        const getIconUrl = (size: string) => {
          // For iPhone-specific sizes, prioritize the exact matches
          if (size === '180' && customIcons.icon180) return customIcons.icon180;
          if (size === '120' && customIcons.icon120) return customIcons.icon120;
          
          // Check if we have the exact size
          if (customIcons[`icon${size}`]) {
            return customIcons[`icon${size}`];
          }
          
          // Use dedicated appleTouchIcon if available
          if (customIcons.appleTouchIcon) {
            return customIcons.appleTouchIcon;
          }
          
          // iPhone-optimized fallback logic
          const sizeNum = parseInt(size);
          const availableIcons = Object.keys(customIcons)
            .filter(key => key.startsWith('icon') && /^\d+$/.test(key.replace('icon', '')))
            .map(key => ({
              key,
              size: parseInt(key.replace('icon', '')),
              url: customIcons[key]
            }))
            .filter(icon => icon.url && icon.url.trim() !== '')
            .sort((a, b) => b.size - a.size);
          
          if (availableIcons.length > 0) {
            // For Apple devices, prefer larger icons that scale down well
            const bestMatch = availableIcons.find(icon => icon.size >= sizeNum) || availableIcons[0];
            return bestMatch.url;
          }
          
          // Final fallback - should never use static files, use the best available dynamic icon
          return null; // Don't create icon links for sizes we don't have
        };

        // Update apple-touch-icon links with iPhone-optimized sizes
        const appleTouchIconSizes = [57, 60, 72, 76, 114, 120, 144, 152, 180];
        
        // Remove existing apple-touch-icon links
        const existingLinks = document.querySelectorAll('link[rel*="apple-touch-icon"]');
        existingLinks.forEach(link => link.remove());

        // Add new apple-touch-icon links with priority for iPhone sizes (only if we have a valid URL)
        appleTouchIconSizes.forEach(size => {
          const iconUrl = getIconUrl(size.toString());
          if (iconUrl) {
            const link = document.createElement('link');
            link.rel = 'apple-touch-icon';
            link.sizes = `${size}x${size}`;
            link.href = iconUrl;
            document.head.appendChild(link);
            console.log(`[PWA] Added apple-touch-icon ${size}x${size}: ${iconUrl}`);
          }
        });

        // Add a default apple-touch-icon for maximum compatibility
        const defaultIconUrl = getIconUrl('180'); // Use 180px as default for modern iPhones
        if (defaultIconUrl) {
          const defaultLink = document.createElement('link');
          defaultLink.rel = 'apple-touch-icon';
          defaultLink.href = defaultIconUrl;
          document.head.appendChild(defaultLink);
          console.log(`[PWA] Added default apple-touch-icon: ${defaultIconUrl}`);
        }
      }
    } catch (error) {
      console.error('[PWA] Error updating Apple touch icons:', error);
    }
  };

  const updateThemeColor = async () => {
    try {
      const { data: settings } = await supabase
        .from('admin_settings')
        .select('settings')
        .eq('id', 1)
        .single();

      const themeColor = settings?.settings?.pwa?.manifest?.themeColor || '#91DA73';
      
      // Update theme-color meta tag
      let themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (!themeColorMeta) {
        themeColorMeta = document.createElement('meta');
        themeColorMeta.setAttribute('name', 'theme-color');
        document.head.appendChild(themeColorMeta);
      }
      themeColorMeta.setAttribute('content', themeColor);

      console.log('PWA: Updated theme color to', themeColor);
    } catch (error) {
      console.error('Error updating theme color:', error);
    }
  };

  const refreshPWAAssets = () => {
    updateManifestLink();
    updateAppleTouchIcons();
    updateThemeColor();
    
    // Clear service worker cache for PWA assets
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_PWA_CACHE'
      });
    }
  };

  useEffect(() => {
    // Initial setup
    refreshPWAAssets();

    // Listen for PWA settings changes
    const channel = supabase
      .channel('pwa-settings-changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'admin_settings'
      }, () => {
        console.log('PWA: Admin settings changed, refreshing PWA assets');
        setTimeout(refreshPWAAssets, 1000); // Small delay to ensure database is updated
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    refreshPWAAssets
  };
};